// React i18n bindings.
//
// API:
//   <LanguageProvider>...</LanguageProvider>          — wrap once at root
//   const t = useT();                                  — t('common.save')
//   const { locale, setLocale } = useLocale();         — read/write current locale
//
// Decision (Session 19A): single Context exposing { locale, dictionary, setLocale }.
// `useT` is a thin wrapper that returns a key→string fn (stable identity per locale)
// so components can use it as a value instead of destructuring the context.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  DEFAULT_LOCALE,
  getDictionary,
  type Dictionary,
  type DictionaryKey,
  type Locale,
} from '@examforge/shared/i18n';
import { readPersistedLocale, writePersistedLocale } from './storage.js';

interface LanguageContextValue {
  locale: Locale;
  dictionary: Dictionary;
  setLocale: (next: Locale) => void;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

export interface LanguageProviderProps {
  children: ReactNode;
  /** Override initial locale (mostly for tests). Production reads from localStorage. */
  initialLocale?: Locale;
}

export function LanguageProvider({ children, initialLocale }: LanguageProviderProps) {
  const [locale, setLocaleState] = useState<Locale>(
    () => initialLocale ?? readPersistedLocale(),
  );

  // Keep <html lang> in sync so the browser + screen readers pick the right font / voice.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.lang = locale === 'zh' ? 'zh-CN' : 'en';
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    writePersistedLocale(next);
  }, []);

  const dictionary = useMemo(() => getDictionary(locale), [locale]);

  const value = useMemo<LanguageContextValue>(
    () => ({ locale, dictionary, setLocale }),
    [locale, dictionary, setLocale],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

function useLanguageContext(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    // Strict mode: a useT() outside provider is a setup bug, not a recoverable state.
    throw new Error(
      'useT / useLocale called outside <LanguageProvider>. Wrap your tree at main.tsx.',
    );
  }
  return ctx;
}

/** The lookup function returned by useT(). Shared so prop-drilled `t` params
 *  (e.g. phase components receiving t from a parent) don't have to redeclare
 *  the signature — and stay in lockstep when params is extended. */
export type TFunction = (key: DictionaryKey, params?: Record<string, string | number>) => string;

/**
 * Returns a stable lookup function `t(key, params?)`. Identity changes only on
 * locale switch.
 *
 * params (optional): interpolates `{name}` placeholders in the dictionary
 * value, e.g. t('diagnose.statsBasisLabel', { total: 12 }). Uses replaceAll so
 * repeated placeholders are all substituted. In dev, warns if the result still
 * contains a `{...}` token (a placeholder the caller forgot to supply or that
 * the dictionary renamed — a silent-failure guard that bare .replace lacked).
 */
export function useT(): TFunction {
  const { dictionary } = useLanguageContext();
  return useCallback((key: DictionaryKey, params?: Record<string, string | number>) => {
    let result = dictionary[key] ?? key;
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        result = result.replaceAll(`{${k}}`, String(v));
      }
    }
    if (import.meta.env.DEV && /\{[^}]+\}/.test(result)) {
      // A placeholder survived — either the caller passed params but missed
      // one, or the dictionary value has a placeholder the caller didn't
      // supply. Surface it in dev so it gets fixed (bare .replace silently
      // leaked the literal {xxx} to the user).
      // eslint-disable-next-line no-console -- i18n config error signal — must be visible in production
      console.warn(`[i18n] unresolved placeholder in "${key}": ${result}`);
    }
    return result;
  }, [dictionary]);
}

/** Read + write the current locale. */
export function useLocale(): { locale: Locale; setLocale: (next: Locale) => void } {
  const { locale, setLocale } = useLanguageContext();
  return { locale, setLocale };
}

export { DEFAULT_LOCALE };
export type { Locale, DictionaryKey };
